import { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import cn from 'classnames';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { Spinner } from '@Components/Spinner';
import { useSetError } from '@src/redux/modals';
import { CpsClientSearchResult } from './cps-utils';
import { awaitJSON, ClientToolsLambdaFetch } from '../../utils/fetch-utils';

interface ClientSearchComboboxProps {
  value: CpsClientSearchResult | null;
  onChange: (client: CpsClientSearchResult | null) => void;
  disabled?: boolean;
}

const ClientSearchCombobox = ({ value, onChange, disabled = false }: ClientSearchComboboxProps) => {
  const [query, setQuery] = useState(value?.name ?? '');
  const [prevValue, setPrevValue] = useState(value);
  const [results, setResults] = useState<CpsClientSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  if (prevValue !== value) {
    setPrevValue(value);
    setQuery(value?.name ?? '');
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const setError = useSetError();

  const search = useCallback(
    async (searchQuery: string) => {
      try {
        setLoading(true);
        const res = await ClientToolsLambdaFetch(
          `/cps/clients?name=${encodeURIComponent(searchQuery)}&active=true`
        );
        const data = await awaitJSON<CpsClientSearchResult[]>(res);
        setResults(data);
        setShowDropdown(true);
      } catch (e) {
        setResults([]);
        setError({ title: "Couldn't search clients", message: e.message, reportError: e });
      } finally {
        setLoading(false);
      }
    },
    [setError]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setQuery(val);

      if (value) {
        onChange(null);
      }

      clearTimeout(debounceRef.current);
      if (val.length >= 3) {
        debounceRef.current = setTimeout(() => {
          search(val);
        }, 300);
      } else {
        setResults([]);
        setShowDropdown(false);
      }
    },
    [value, onChange, search]
  );

  const handleSelect = useCallback(
    (client: CpsClientSearchResult) => {
      onChange(client);
      setQuery(client.name);
      setShowDropdown(false);
    },
    [onChange]
  );

  return (
    <div ref={containerRef} className='cps-client-search'>
      <Form.Control
        type='text'
        value={query}
        onChange={handleInputChange}
        placeholder='Search clients...'
        disabled={disabled}
        className='cps-client-search-input'
      />
      {loading && (
        <div className='cps-client-search-spinner'>
          <Spinner />
        </div>
      )}
      {showDropdown && results.length > 0 && (
        <ul className='cps-client-search-dropdown'>
          {results.map(client => (
            <li key={client.id}>
              <Button
                size={Size.LG}
                appearance={Appearance.EMPTY}
                className={cn('cps-client-search-option', { active: value?.id === client.id })}
                onClick={() => handleSelect(client)}
              >
                {client.name}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {showDropdown && !loading && results.length === 0 && query.length >= 3 && (
        <ul className='cps-client-search-dropdown'>
          <li className='cps-client-search-no-results'>No clients found</li>
        </ul>
      )}
    </div>
  );
};

export { ClientSearchCombobox };
